import type {
  AssetDownloadMetadataResponse,
  CreativeBriefFilterCountsResponse,
  GetCreativeBriefsResponse
} from '@innovationdepartment/proxima-sdk-axios';
import { AxiosError } from 'axios';
import useProximaSDK from 'hooks/useProximaSDK';
import { useBrandStore } from 'stores';

// ─── Error formatting ────────────────────────────────────────────────────────

function formatSdkError(error: unknown): string {
  if (error instanceof AxiosError) {
    const status = error.response?.status ?? 'unknown';
    const data = error.response?.data;
    const bodyMessage =
      data != null &&
      typeof data === 'object' &&
      'message' in data &&
      String((data as { message: unknown }).message).trim();
    return bodyMessage
      ? `Request failed (${status}): ${bodyMessage}`
      : `Request failed (${status})`;
  }
  return error instanceof Error ? error.message : 'Request failed';
}

// ─── Result types ────────────────────────────────────────────────────────────

export type GetBriefsResult =
  | { data: GetCreativeBriefsResponse; error?: undefined }
  | { data?: undefined; error: string };

export type GetFilterCountsResult =
  | { data: CreativeBriefFilterCountsResponse; error?: undefined }
  | { data?: undefined; error: string };

export type UpdateVisibilityResult =
  | { success: true; error?: undefined }
  | { success?: undefined; error: string };

export type GetAssetDownloadUrlResult =
  | { data: AssetDownloadMetadataResponse; error?: undefined }
  | { data?: undefined; error: string };

// ─── Params ──────────────────────────────────────────────────────────────────

export type GetBriefsParams = {
  pageSize?: number;
  pageNumber?: number;
  sortDirection?: 'asc' | 'desc';
  strategyIds?: string[];
  productIds?: number[];
  formats?: string[];
  months?: string[];
  visibleToCustomerOnly?: boolean;
};

// ─── Hook ────────────────────────────────────────────────────────────────────

// we show 3 briefs per row, so 21 briefs per page is a good default
const DEFAULT_PAGE_SIZE = 21;
const DEFAULT_PAGE_NUMBER = 1;

function useCreativeBriefsApi() {
  const creativeEngineApi = useProximaSDK('CreativeEngineApi');
  const { brand } = useBrandStore();
  const { brandId } = brand;

  const getCreativeBriefsInternal = async (params?: GetBriefsParams): Promise<GetBriefsResult> => {
    if (!brandId) return { error: 'Missing brandId' };

    const pageSize = params?.pageSize ?? DEFAULT_PAGE_SIZE;
    const pageNumber = params?.pageNumber ?? DEFAULT_PAGE_NUMBER;

    try {
      const response = await creativeEngineApi.getCreativeBriefsInternal({
        brandId,
        pageSize,
        pageNumber,
        createdAtSortDirection: params?.sortDirection,
        strategyIds: params?.strategyIds?.length ? params.strategyIds.join(',') : undefined,
        productIds: params?.productIds?.length ? params.productIds.join(',') : undefined,
        formats: params?.formats?.length ? params.formats.join(',') : undefined,
        months: params?.months?.length ? params.months.join(',') : undefined,
        visibleToCustomerOnly: params?.visibleToCustomerOnly
      });
      const { data } = response;
      if (data == null) return { error: 'No data in response' };
      return { data };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  const getFilterCountsInternal = async (
    visibleToCustomerOnly?: boolean
  ): Promise<GetFilterCountsResult> => {
    if (!brandId) return { error: 'Missing brandId' };

    try {
      const response = await creativeEngineApi.getCreativeBriefFilterCountsInternal({
        brandId,
        visibleToCustomerOnly
      });
      const { data } = response;
      if (data == null) return { error: 'No data in response' };
      return { data };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  const updateBriefVisibility = async (
    briefId: string,
    visibleToCustomer: boolean
  ): Promise<UpdateVisibilityResult> => {
    if (!brandId) return { error: 'Missing brandId' };

    try {
      await creativeEngineApi.updateCreativeBriefVisibility({
        brandId,
        briefId,
        updateBriefVisibilityRequest: { visibleToCustomer }
      });
      return { success: true };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  const getAssetDownloadUrl = async (params: {
    briefId: string;
    assetId: number;
    overlay: boolean;
  }): Promise<GetAssetDownloadUrlResult> => {
    if (!brandId) return { error: 'Missing brandId' };

    try {
      const response = await creativeEngineApi.getCreativeBriefAssetDownloadMetadata({
        brandId,
        briefId: params.briefId,
        assetId: params.assetId,
        overlay: params.overlay
      });
      const { data } = response;
      if (data == null) return { error: 'No data in response' };
      return { data };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  return {
    getCreativeBriefsInternal,
    getFilterCountsInternal,
    updateBriefVisibility,
    getAssetDownloadUrl,
    loading: creativeEngineApi.loading,
    brandId
  };
}

export default useCreativeBriefsApi;
